import { ReactNode } from "react";
import { Button } from "antd";
import { ApiError } from "../../hooks/apiRequest";
import { EmptyState, TableSkeleton } from "../../components/ui/States";

interface Props {
  /** Pass a query's `error` value directly (falsy/undefined when there is none). */
  error?: unknown;
  isLoading?: boolean;
  /** Shown instead of the default skeleton while isLoading is true. */
  loadingFallback?: ReactNode;
  /** Shown when `error` is set but is not an ApiError (e.g. a network failure). */
  errorFallback?: string;
  children: ReactNode;
}

/**
 * Shared error/loading wrapper. Same contract as before — only the visual
 * treatment changed: a designed error block instead of an antd Alert, and a
 * shape-preserving skeleton instead of a centred Spin.
 */
export const DisplayComponent = ({
  error,
  isLoading,
  loadingFallback,
  errorFallback = "Something went wrong loading this page. Please try again.",
  children,
}: Props) => {
  if (error) {
    return (
      <EmptyState
        tone="error"
        title="This didn't load"
        description={error instanceof ApiError ? error.message : errorFallback}
        actions={<Button onClick={() => window.location.reload()}>Retry</Button>}
      />
    );
  }
  if (isLoading) return <>{loadingFallback ?? <TableSkeleton />}</>;
  return <>{children}</>;
};
